import React, { useState, useEffect, useMemo } from 'react';
import MacTable from '../ui/MacTable';
import VisitLink from '../ui/VisitLink';
import '../../styles/style.css';

// Import utilities
import { getData } from '../../utils/dataUtils';
import { formatDate } from '../../utils/dateUtils';
import { TABLE_HEADERS } from '../../utils/constants';

// Group bounties by platform (pure function, hoisted outside component)
const groupBountiesByPlatform = (bountiesList) => {
  return bountiesList.reduce((groups, bounty) => {
    const platform = bounty.platform || 'Other';
    if (!groups[platform]) {
      groups[platform] = {
        bounties: [],
        url: bounty.platformURL || ''
      };
    } else if (bounty.platformURL && !groups[platform].url) {
      groups[platform].url = bounty.platformURL;
    }
    groups[platform].bounties.push(bounty);
    return groups;
  }, {});
};

const BugBountySection = () => {
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    // Helper function to initialize group expansion state
    const initializeGroups = (bountiesList) => {
      const initialGroupState = {};
      const grouped = groupBountiesByPlatform(bountiesList);
      Object.keys(grouped).forEach(platform => {
        initialGroupState[platform] = false;
      });
      setExpandedGroups(initialGroupState);
    };

    const loadBounties = async () => {
      try {
        setLoading(true);
        
        try {
          // Get data from local JSON file with sorting
          const data = getData('bounties', { sort: 'date', sortOrder: 'desc' });
          setBounties(data);
          initializeGroups(data);
        } catch (err) {
          setError('Failed to load bug bounty data.');
        }
        
        setLoading(false);
      } catch (err) {
        setError('An unexpected error occurred.');
        setLoading(false);
      }
    };

    loadBounties();
  }, []);

  const toggleGroupExpand = (platform, e) => {
    e.preventDefault();
    setExpandedGroups(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  const groupedBounties = useMemo(() => groupBountiesByPlatform(bounties), [bounties]);

  const renderRow = (item, index) => (
    <tr key={item.id || index}>
      <td>
        {item.projectURL ? 
          <a href={item.projectURL} target="_blank" rel="noopener noreferrer">{item.project}</a> : 
          item.isPublished ? item.project : <img src="images/clock-face-four-oclock_1f553.gif" alt="clock" loading="lazy" width="16" height="16" />
        }
      </td>
      <td>
        <span className={`severity ${item.severity?.toLowerCase()}`}>
          {item.severity}
        </span>
      </td>
      <td>{formatDate(item.date)}</td>
      <td>
        {item.reportURL ? (
          <VisitLink url={item.reportURL} placeholder="view" />
        ) : (
          <span className="unpublished">{item.isPublished === false ? 'Pending Approval' : 'N/A'}</span>
        )}
      </td>
    </tr>
  );

  if (loading) {
    return (
      <section id="bugbounty">
        <div className="container">
          <h2 className="topic">Bug Bounty</h2>
          <div className="loading">Loading bug bounties...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="bugbounty">
        <div className="container">
          <h2 className="topic">Bug Bounty</h2>
          <div className="error">{error}</div>
        </div>
      </section>
    );
  }

  return (
    <section id="bugbounty">
      <div className="container">
        <h2 className="topic">Bug Bounty</h2>

        {/* Render a separate table for each platform */}
        {Object.entries(groupedBounties).map(([platform, platformData]) => {
          // Filter bounties based on expanded state
          const displayBounties = expandedGroups[platform]
            ? platformData.bounties
            : platformData.bounties.slice(0, 5);
            
          return (
            <div key={platform} className="bounty-group">
              <MacTable 
                title={
                  <>
                    {platform} 
                    {platformData.url && (
                      <> | <VisitLink url={platformData.url} placeholder="visit" /></>
                    )}
                  </>
                }
                headers={TABLE_HEADERS.BUG_BOUNTY} 
                data={displayBounties} 
                renderRow={renderRow}
                showViewMore={platformData.bounties.length > 5}
                onViewMoreClick={(e) => toggleGroupExpand(platform, e)}
                viewMoreText={expandedGroups[platform] ? 'Show Less' : 'View More'}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default BugBountySection;
